import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money } from "@/lib/helpers";
import MethodBreakdown from "@/components/MethodBreakdown";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, Scale } from "lucide-react";

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-card border border-border rounded-2xl shadow-card p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${tint}1a`, color: tint }}><Icon className="h-5 w-5" /></div>
        <div className="text-xs uppercase tracking-overline text-muted-foreground">{label}</div>
      </div>
      <div className="font-display font-black text-3xl mt-3 tracking-tight" style={{ color: tint }}>{value}</div>
    </div>
  );
}

const monthName = (d) => {
  const s = d.toLocaleDateString("fr-FR", { month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function periodLabel(period, start, end) {
  if (!start || !end) return "";
  const s = new Date(start);
  const lastDay = new Date(end);
  lastDay.setDate(lastDay.getDate() - 1);
  const year = s.getFullYear();
  if (period === "year") return `Année ${year}`;
  if (period === "quarter") return `${Math.floor(s.getMonth() / 3) + 1}ᵉ trimestre ${year} · ${monthName(s)} – ${monthName(lastDay)}`;
  if (period === "semester") return `${Math.floor(s.getMonth() / 6) + 1 === 1 ? "1er" : "2ᵉ"} semestre ${year} · ${monthName(s)} – ${monthName(lastDay)}`;
  return `${monthName(s)} ${year}`;
}

export default function Profitability() {
  const { settings } = useSettings();
  const cur = settings?.currency || "EUR";
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/profitability", { params: { month, period } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [month, period]);
  useEffect(() => { load(); }, [load]);

  const positive = (data?.result ?? 0) >= 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Comptabilité</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Rentabilité</h1>
        </div>
        {/* flex-wrap : sur téléphone et tablette, les onglets passent sous le mois au lieu de sortir de l'écran. */}
        <div className="flex flex-wrap items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 px-2.5 rounded-lg border border-border bg-card text-sm" data-testid="profitability-month" />
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="profitability-period-tabs">
              <TabsTrigger value="month" data-testid="period-month">Mois</TabsTrigger>
              <TabsTrigger value="quarter" data-testid="period-quarter">Trimestre</TabsTrigger>
              <TabsTrigger value="semester" data-testid="period-semester">Semestre</TabsTrigger>
              <TabsTrigger value="year" data-testid="period-year">Année</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {data && (
        <>
          <p className="text-sm text-muted-foreground" data-testid="profitability-period-label">{periodLabel(data.period, data.start, data.end)}</p>
          {/* 3 cases côte à côte à partir de 1024 px ; en dessous, une par ligne (à 768 px, un gros montant sortait de sa case). */}
          <div className="grid lg:grid-cols-3 gap-4">
            <Kpi icon={TrendingUp} label="Rentrées" value={money(data.revenue, cur)} tint="#10b981" />
            <Kpi icon={TrendingDown} label="Dépenses" value={money(data.expenses, cur)} tint="#ef4444" />
            <Kpi icon={Scale} label="Résultat" value={money(data.result, cur)} tint={positive ? "#10b981" : "#ef4444"} />
          </div>
          {/* Transport (lot T6) : « Dépenses » = frais de société + commissions des concierges ; le détail, à part. Le serveur
              n'envoie `commissions` qu'en transport : chez M CARWASH, rien ne s'affiche. */}
          {data.commissions !== undefined && <p className="text-sm text-muted-foreground" data-testid="profitability-commissions">Dépenses : frais de société <span className="font-medium text-foreground">{money(data.company_expenses, cur)}</span> · commissions des concierges <span className="font-medium text-foreground" data-testid="profitability-commissions-amount">{money(data.commissions, cur)}</span>{data.commissions_count > 0 ? ` (${data.commissions_count} course${data.commissions_count > 1 ? "s" : ""}, à la date de la course)` : ""}</p>}
          {data.advances > 0 && <p className="text-sm text-muted-foreground" data-testid="profitability-advances">Avances carburant (hors CA) : <span className="font-medium text-foreground">{money(data.advances, cur)}</span> encaissées sur la période, non comptées dans les rentrées.</p>}
          <MethodBreakdown byMethod={data.by_method} revenue={data.revenue} cur={cur} />
        </>
      )}
    </div>
  );
}
